import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { COPY } from "@/lib/i18n";
import { JSONLD } from "@/lib/intel-data";
import useTrackingScripts from "@/lib/useTrackingScripts";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Chat from "@/components/Chat";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import Skills from "@/components/Skills";
import About from "@/components/About";
import LeadCapture from "@/components/LeadCapture";
import Footer from "@/components/Footer";
import Privacy from "@/components/Privacy";
import IntelHub from "@/pages/IntelHub";
import AdminConsole from "@/pages/AdminConsole";

function detectLang() {
  if (typeof navigator !== "undefined") {
    const l = (navigator.language || "en").toLowerCase();
    if (l.startsWith("no") || l.startsWith("nb") || l.startsWith("nn") || l.startsWith("da") || l.startsWith("sv")) {
      return "no";
    }
  }
  return "en";
}

const Home = () => {
  const [lang, setLang] = useState("en");
  const [sessionId, setSessionId] = useState(null);
  useTrackingScripts();

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("np-lang") : null;
    setLang(stored || detectLang());
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("np-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // Inject Person + Organization JSON-LD + canonical so the warm site is L2/L3-citable too.
  useEffect(() => {
    const setCanon = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      return link;
    };
    const canon = setCanon("https://nickolass.com/");

    const personScript = document.createElement("script");
    personScript.type = "application/ld+json";
    personScript.setAttribute("data-np-jsonld", "person");
    personScript.text = JSON.stringify(JSONLD.person);
    document.head.appendChild(personScript);

    const orgScript = document.createElement("script");
    orgScript.type = "application/ld+json";
    orgScript.setAttribute("data-np-jsonld", "org");
    orgScript.text = JSON.stringify(JSONLD.organization);
    document.head.appendChild(orgScript);

    return () => {
      document.head.removeChild(personScript);
      document.head.removeChild(orgScript);
      if (canon) canon.setAttribute("href", window.location.href);
    };
  }, []);

  const t = COPY[lang] || COPY.en;

  return (
    <div className="App">
      <Nav lang={lang} setLang={setLang} t={t} />
      <main>
        <Hero t={t} />
        <Chat t={t} lang={lang} sessionId={sessionId} setSessionId={setSessionId} />
        <Skills t={t} />
        <About t={t} />
        <LeadCapture t={t} lang={lang} sessionId={sessionId} />
      </main>
      <Footer t={t} />
      <Toaster richColors position="top-center" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/personvern" element={<Privacy />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/intel" element={<IntelHub />} />
        <Route path="/intel/console" element={<AdminConsole />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
